import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Eye, Copy, Check, Flame } from 'lucide-react';
import { classicQuestionsApi } from '../../services/classicQuestionsApi';

const Container = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 60px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #667eea;
  font-size: 16px;
  cursor: pointer;
  padding: 8px 0;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    color: #5a67d8;
    transform: translateX(-2px);
  }
`;

const QuestionCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
  flex: 1;
  min-width: 0;
  max-width: 900px;
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const QuestionTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.4;
  margin: 0;
  flex: 1;
  margin-right: 20px;
`;


const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
`;

const Tag = styled.span`
  background: ${props => {
    switch(props.type) {
      case 'category': return '#e6f3ff';
      case 'difficulty': return props.difficulty === 'easy' ? '#f0f9ff' : props.difficulty === 'medium' ? '#fef3c7' : '#fef2f2';
      case 'normal': return '#f7fafc';
      default: return '#f7fafc';
    }
  }};
  border: 1px solid ${props => {
    switch(props.type) {
      case 'category': return '#b3d9ff';
      case 'difficulty': return props.difficulty === 'easy' ? '#7dd3fc' : props.difficulty === 'medium' ? '#fbbf24' : '#fca5a5';
      case 'normal': return '#e2e8f0';
      default: return '#e2e8f0';
    }
  }};
  color: ${props => {
    switch(props.type) {
      case 'category': return '#1e40af';
      case 'difficulty': return props.difficulty === 'easy' ? '#0369a1' : props.difficulty === 'medium' ? '#d97706' : '#dc2626';
      case 'normal': return '#4a5568';
      default: return '#4a5568';
    }
  }};
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  height: 36px;
  min-width: fit-content;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
`;

const InfoButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  height: 36px;
  min-width: fit-content;
`;


const ContentSection = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const SectionIcon = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color || '#667eea'};
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
`;

const SectionContent = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  line-height: 1.7;
  color: #2d3748;
  font-size: 16px;

  strong {
    color: #1a202c;
    font-weight: 600;
  }

  p {
    margin: 0 0 16px 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  ol, ul {
    margin: 0 0 16px 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  color: #4a5568;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 16px;

  &:hover {
    background: #edf2f7;
    border-color: #cbd5e0;
  }

  &.copied {
    background: #f0fff4;
    border-color: #9ae6b4;
    color: #22543d;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 18px;
  color: #6b7280;
  width: 100%;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #6b7280;
  font-size: 16px;
  width: 100%;
`;

export default function QuestionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const lastViewedIdRef = React.useRef(null);

  useEffect(() => {
    const fetchQuestion = async () => {
      setLoading(true);
      
      try {
        let apiData;
        // Check if we should increment view (avoid double count in StrictMode)
        const shouldIncrement = lastViewedIdRef.current !== id;
        
        if (shouldIncrement) {
          lastViewedIdRef.current = id;
          apiData = await classicQuestionsApi.incrementView(id);
        } else {
          apiData = await classicQuestionsApi.getQuestion(id);
        }

        if (apiData) {
          // 映射后端字段到前端组件使用的字段
          const mappedData = {
            ...apiData,
            explanation: apiData.guidance || apiData.explanation,
            answerExample: apiData.sample_answer || apiData.answerExample
          };
          setQuestion(mappedData);
        }
      } catch (error) {
        console.error('Failed to fetch question:', error);
        setQuestion(null);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestion();
  }, [id]);

  const handleBack = () => {
    navigate('/classic-questions');
  };

  const handleCopyAnswer = async () => {
    if (question?.answerExample) {
      await navigator.clipboard.writeText(question.answerExample);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };


  const getDifficultyColor = (difficulty) => {
    const colorMap = {
      easy: '#10b981',
      medium: '#f59e0b',
      hard: '#ef4444'
    };
    return colorMap[difficulty] || '#6b7280';
  };

  const renderDifficultyFlames = (difficulty) => {
    if (!difficulty) return null;

    const difficultyConfig = {
      easy: { count: 1, class: 'easy' },
      medium: { count: 2, class: 'medium' },
      hard: { count: 3, class: 'hard' }
    };

    const config = difficultyConfig[difficulty] || difficultyConfig.medium;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        {[1, 2, 3].map((flame) => (
          <Flame
            key={flame}
            size={12}
            color={flame <= config.count ? getDifficultyColor(difficulty) : '#e5e7eb'}
            fill={flame <= config.count ? getDifficultyColor(difficulty) : '#e5e7eb'}
          />
        ))}
      </div>
    );
  };

  const renderMarkdown = (text) => {
    if (!text) return '';
    
    // 处理换行
    const lines = text.split('\n');
    const processedLines = lines.map((line, index) => {
      // 处理粗体 **text**
      let processedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      // 处理数字列表 1. 或 2. 等
      if (/^\d+\.\s/.test(processedLine)) {
        return `<li>${processedLine.replace(/^\d+\.\s/, '')}</li>`;
      }
      
      // 处理普通段落
      if (processedLine.trim()) {
        return `<p>${processedLine}</p>`;
      }
      
      return processedLine;
    });
    
    // 将连续的li元素包装在ol中
    const result = [];
    let currentList = [];
    
    processedLines.forEach((line, index) => {
      if (line.startsWith('<li>')) {
        currentList.push(line);
      } else {
        if (currentList.length > 0) {
          result.push(`<ol>${currentList.join('')}</ol>`);
          currentList = [];
        }
        if (line.trim()) {
          result.push(line);
        }
      }
    });
    
    if (currentList.length > 0) {
      result.push(`<ol>${currentList.join('')}</ol>`);
    }
    
    return result.join('');
  };

  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <LoadingContainer>加载中...</LoadingContainer>
        </ContentWrapper>
      </Container>
    );
  }

  if (!question) {
    return (
      <Container>
        <ContentWrapper>
          <ErrorContainer>
            <h2>题目未找到</h2>
            <p>抱歉，您要查看的题目不存在或已被删除。</p>
            <BackButton onClick={handleBack}>
              <ArrowLeft size={20} />
              返回
            </BackButton>
          </ErrorContainer>
        </ContentWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <ContentWrapper>
        <BackButton onClick={handleBack}>
          <ArrowLeft size={20} />
          返回
        </BackButton>

        <QuestionCard>
          <QuestionHeader>
            <QuestionTitle>{question.question}</QuestionTitle>
          </QuestionHeader>

          <TagsContainer>
            {Array.isArray(question.tags) && question.tags
              .filter(tag => tag !== question.category)
              .map((tag, index) => (
                <Tag key={index} type="normal">{tag}</Tag>
              ))}
          </TagsContainer>

          <ActionBar>
            <ButtonGroup>
              <ActionButton>
                <Eye size={16} />
                {question.views || 0}
              </ActionButton>
              {question.category && (
                <InfoButton>
                  <span>{question.category}</span>
                </InfoButton>
              )}
              <InfoButton>
                {renderDifficultyFlames(question.difficulty)}
              </InfoButton>
            </ButtonGroup>
          </ActionBar>

          <ContentSection>
            <SectionHeader>
              <SectionIcon color="#8b5cf6" />
              <SectionTitle>题目解析</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <div dangerouslySetInnerHTML={{ __html: question.explanation || '暂无解析内容' }} />
            </SectionContent>
          </ContentSection>

          <ContentSection>
            <SectionHeader>
              <SectionIcon color="#10b981" />
              <SectionTitle>回答示例</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <div dangerouslySetInnerHTML={{ __html: renderMarkdown(question.answerExample) || '暂无回答示例' }} />
            </SectionContent>
            <CopyButton 
              onClick={handleCopyAnswer}
              className={copied ? 'copied' : ''}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? '已复制' : '复制回答'}
            </CopyButton>
          </ContentSection>
        </QuestionCard>
      </ContentWrapper>
    </Container>
  );
}
